import { useState } from "react";
import { DEPARTMENTS } from "../lib/departments";
import {
  PLACEMENT_BATCHES,
  deptTotals,
  recruiterTotals,
} from "../lib/placements";
import { latestPlacementYear } from "../lib/placementTrends";
import {
  PLACEMENT_CONTACTS,
  PLACEMENT_FACILITIES,
  PLACEMENT_VENUES,
  RECRUITER_PROCESS,
} from "../lib/placementCell";
import { Figure } from "../components/Figure";
import { RecruiterDirectory } from "../components/sections/RecruiterDirectory";

/**
 * Figures here come from the Training & Placement Cell's own record
 * (placements.ts). What was on this page before was invented and considerably
 * overstated: "1,142 offers. 187 recruiters. 96% of registered students
 * placed", a "₹52 LPA" highest package, and a top-recruiter list naming Wipro,
 * Bosch, L&T, Tata Elxsi, Mindtree, KPIT and Mercedes Benz R&D — none of which
 * appear in the Cell's record for either batch. The real 2026 batch is 596
 * offers from 52 companies, topping out at 40 LPA.
 *
 * No placement RATE is quoted: the sheet records selections, not how many
 * students registered, so a percentage cannot be derived from it.
 */
const LATEST = PLACEMENT_BATCHES[0];
const YEAR = latestPlacementYear();
const DEPT_NAME = new Map(DEPARTMENTS.map((d) => [d.code, d.name]));

const summaryFigure = (re: RegExp) => LATEST.summary.match(re)?.[1] ?? "—";


export default function Recruiters() {
  const [submitted, setSubmitted] = useState(false);

  return (
    <>
      <section className="section" style={{ paddingTop: "var(--space-8)" }}>
        <div className="container">
          <div className="rec-hero-grid">
            <div className="rec-hero-card">
              <div className="eyebrow">PLACEMENTS · {YEAR.year} BATCH</div>
              <h2>
                {YEAR.placed} students placed. 180+ recruitment partners.{" "}
                {LATEST.drives.length} drives on campus.
              </h2>
              <p>
                Highest package ₹{summaryFigure(/Highest Package\s*-?\s*([\d.]+)/i)} LPA ·
                Average ₹{summaryFigure(/Average Salary\s*-?\s*([\d.]+)/i)} LPA · Median ₹
                {summaryFigure(/Median Salary\s*-?\s*([\d.]+)/i)} LPA. Most offers by:{" "}
                {recruiterTotals(LATEST).slice(0, 6).map((r) => r.company).join(", ")}.
              </p>
              <div className="stats-mini">
                <div><div className="n" data-count={String(YEAR.placed)}>0</div><div className="l">Students placed</div></div>
                <div><div className="n" data-count="180" data-suffix="+">0</div><div className="l">Recruitment partners</div></div>
                <div><div className="n" data-count={String(deptTotals(LATEST).length)}>0</div><div className="l">Departments</div></div>
              </div>
              <div className="rec-hero-contacts">
                {PLACEMENT_CONTACTS.map((c) => (
                  <div key={c.name} className="rec-hero-contact">
                    <div className="role">{c.role}</div>
                    <div className="name">{c.name}</div>
                    <div className="lines">
                      <a href={`tel:${c.phone.replace(/\s/g, "")}`}>{c.phone}</a>
                      <a href={`mailto:${c.email}`}>{c.email}</a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {submitted ? (
              <div className="rec-form">
                <h3>Brief received</h3>
                <p className="sub">
                  A coordinator will reply within 24 hours at the email address you gave.
                </p>
              </div>
            ) : (
              <form
                className="rec-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  setSubmitted(true);
                }}
              >
                <h3>Post a job / drive</h3>
                <div className="sub">A coordinator will reply within 24 hours.</div>
                <div className="row2">
                  <div><label>Company</label><input required placeholder="Your company"/></div>
                  <div><label>Sector</label><select><option>IT services</option><option>Product</option><option>Core / mfg</option><option>Consulting</option><option>R&D</option><option>Other</option></select></div>
                </div>
                <div className="row2">
                  <div><label>Contact name</label><input required placeholder="Full name"/></div>
                  <div><label>Email</label><input required type="email" placeholder="hr@example.com"/></div>
                </div>
                <div className="row2">
                  <div><label>Role</label><input placeholder="e.g. Software engineer"/></div>
                  <div><label>CTC range</label><input placeholder="e.g. 8–12 LPA"/></div>
                </div>
                <label>Eligible branches</label>
                <select multiple style={{height: 80}}>
                  <option>Computer Science (CSE)</option>
                  <option>Information Science (ISE)</option>
                  <option>Electronics &amp; Comm. (ECE)</option>
                  <option>Mechanical (ME)</option>
                  <option>Civil (CE)</option>
                  <option>Electrical &amp; Electronics (EEE)</option>
                  <option>AI/ML</option>
                  <option>Data Science</option>
                  <option>All branches</option>
                </select>
                <label>Notes</label>
                <textarea rows={3} placeholder="Test format, slot preference, JD link..."></textarea>
                <div className="submit"><button type="submit" className="btn btn-primary">Submit →</button></div>
              </form>
            )}
          </div>

          {/* The Cell's own sequence, replacing four generically-worded steps
              that were written for this page rather than taken from it. */}
          <h2 style={{ marginTop: 80, fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 600 }}>
            How a campus drive works
          </h2>
          <p style={{ maxWidth: "62ch", color: "var(--text-soft)" }}>
            Placement activity opens in July and August, when the Cell writes to its
            recruiting companies for the year.
          </p>
          <div className="exam-process">
            {RECRUITER_PROCESS.map((step) => (
              <div key={step.title} className="exam-process-step reveal">
                <h4>{step.title}</h4>
                <p>{step.body}</p>
              </div>
            ))}
          </div>

          <h2 style={{ marginTop: 80, fontSize: "clamp(28px, 3vw, 40px)", fontWeight: 600 }}>
            What the campus can host
          </h2>
          <p style={{ maxWidth: "62ch", color: "var(--text-soft)" }}>
            The infrastructure the Cell puts behind a drive — halls for the talk, labs for
            the test, rooms for the interviews.
          </p>
          <div className="facility-grid">
            {PLACEMENT_FACILITIES.map((f) => (
              <div key={f.label} className="facility-cell reveal">
                <div className="facility-num">{f.figure}</div>
                <div className="facility-label">{f.label}</div>
              </div>
            ))}
          </div>

          <div className="venue-grid">
            {PLACEMENT_VENUES.map((v) => (
              <figure key={v.media.url} className="venue-card reveal">
                <Figure
                  media={v.media}
                  fallbackLabel={v.title}
                  aspect="4/3"
                  sizes="(max-width: 700px) 92vw, (max-width: 1100px) 46vw, 300px"
                />
                <figcaption>
                  <span className="venue-title">{v.title}</span>
                  <span className="venue-caption">{v.caption}</span>
                </figcaption>
              </figure>
            ))}
          </div>

        </div>
      </section>

      <RecruiterDirectory />
    </>
  );
}
